import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Users, Languages, Plus } from "lucide-react";

const fmtMB = (b) => (b / (1024 * 1024)).toFixed(1);

export default function AdminPage() {
  const { t, languages } = useI18n();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [quotas, setQuotas] = useState({});
  const [editLang, setEditLang] = useState("cs");
  const [refStrings, setRefStrings] = useState({});
  const [langStrings, setLangStrings] = useState({});
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [newLang, setNewLang] = useState({ code: "", name: "" });
  const [allLangs, setAllLangs] = useState(languages);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => {
      setUsers(data);
      setQuotas(Object.fromEntries(data.map((u) => [u.id, Math.round(u.quota_bytes / (1024 * 1024))])));
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/i18n/cs").then(({ data }) => setRefStrings(data)).catch(() => {});
    api.get("/i18n/languages").then(({ data }) => setAllLangs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setEdits({});
    api.get(`/i18n/${editLang}`).then(({ data }) => setLangStrings(data)).catch(() => setLangStrings({}));
  }, [editLang]);

  const saveQuota = async (uid) => {
    try {
      await api.put(`/admin/users/${uid}/quota`, { quota_mb: Number(quotas[uid]) });
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addLanguage = async () => {
    if (!newLang.code || !newLang.name) return;
    try {
      await api.put(`/admin/i18n/${newLang.code.toLowerCase()}`, { name: newLang.name, strings: {} });
      const { data } = await api.get("/i18n/languages");
      setAllLangs(data);
      setEditLang(newLang.code.toLowerCase());
      setNewLang({ code: "", name: "" });
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveTranslations = async () => {
    if (!Object.keys(edits).length) return;
    try {
      await api.put(`/admin/i18n/${editLang}`, { strings: edits });
      setLangStrings({ ...langStrings, ...edits });
      setEdits({});
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const keys = Object.keys(refStrings).filter((k) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return k.toLowerCase().includes(s) || (refStrings[k] || "").toLowerCase().includes(s) || (langStrings[k] || "").toLowerCase().includes(s);
  });

  const inputCls = "w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500";

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 pt-28 pb-20" data-testid="admin-page">
        <h1 className="text-3xl font-extrabold tracking-tight text-white">{t("admin.title")}</h1>
        <div className="mt-8 flex gap-2 border-b border-slate-800">
          {[{ id: "users", label: t("admin.tab_users"), icon: Users }, { id: "i18n", label: t("admin.tab_i18n"), icon: Languages }].map((x) => (
            <button key={x.id} data-testid={`admin-tab-${x.id}`} onClick={() => setTab(x.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === x.id ? "border-indigo-500 text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              <x.icon className="w-4 h-4" /> {x.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="mt-8 space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.email}`} className="flex flex-wrap items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-3.5">
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-semibold text-white">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email} · {u.role}</div>
                </div>
                <span className="text-xs text-slate-400 font-mono">{t("admin.usage")}: {fmtMB(u.usage_bytes)} MB</span>
                <div className="flex items-center gap-2">
                  <input data-testid={`admin-quota-input-${u.email}`} type="number" min="0" value={quotas[u.id] ?? ""}
                    onChange={(e) => setQuotas({ ...quotas, [u.id]: e.target.value })}
                    className="w-24 px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm text-white font-mono focus:outline-none focus:border-indigo-500" />
                  <span className="text-xs text-slate-500">MB</span>
                  <button data-testid={`admin-quota-save-${u.email}`} onClick={() => saveQuota(u.id)}
                    className="px-4 py-2 rounded-full bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-500 transition-colors">
                    {t("admin.save")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "i18n" && (
          <div className="mt-8 space-y-5">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("admin.lang_select")}</label>
                <select data-testid="admin-lang-select" value={editLang} onChange={(e) => setEditLang(e.target.value)}
                  className="mt-2 block px-4 py-2.5 rounded-xl bg-[#0B0F17] border border-slate-700 text-white focus:outline-none focus:border-indigo-500">
                  {allLangs.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-xs text-slate-500">{t("admin.lang_code")}</label>
                  <input data-testid="admin-new-lang-code" value={newLang.code} onChange={(e) => setNewLang({ ...newLang, code: e.target.value })} className={`mt-1 w-24 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{t("admin.lang_name")}</label>
                  <input data-testid="admin-new-lang-name" value={newLang.name} onChange={(e) => setNewLang({ ...newLang, name: e.target.value })} className={`mt-1 w-36 ${inputCls}`} />
                </div>
                <button data-testid="admin-add-lang-button" onClick={addLanguage}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-700 text-slate-300 text-sm font-semibold hover:border-indigo-500 transition-colors">
                  <Plus className="w-4 h-4" /> {t("admin.new_lang")}
                </button>
              </div>
              <button data-testid="admin-save-translations" onClick={saveTranslations} disabled={!Object.keys(edits).length}
                className="ml-auto px-6 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-40">
                {t("admin.save_translations")} {Object.keys(edits).length > 0 && `(${Object.keys(edits).length})`}
              </button>
            </div>
            <input data-testid="admin-i18n-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.search_key")} className={inputCls} />
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
              {keys.slice(0, 200).map((k) => (
                <div key={k} className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-[#131B2A] border border-slate-800 rounded-xl p-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-indigo-300 truncate">{k}</div>
                    <div className="text-xs text-slate-400 mt-1">{refStrings[k]}</div>
                  </div>
                  <textarea rows={2} value={edits[k] ?? langStrings[k] ?? ""}
                    onChange={(e) => setEdits({ ...edits, [k]: e.target.value })}
                    className={`${inputCls} resize-none`} />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
